import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { 
  configApiRef, 
  useApi, 
  microsoftAuthApiRef
} from '@backstage/core-plugin-api';
import { Progress, UserIdentity } from '@backstage/core-components';
import './hideHeaderLogin.css';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';

// Mantén tus estilos personalizados
const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'stretch',
    justifyContent: 'space-between',
    height: '100vh',
    width: '100%',
    overflow: 'hidden',
    padding: 0,
    margin: 0,
    border: 'none',
    boxSizing: 'border-box',
  },
  microsoftButton: {
    backgroundColor: '#FFDD00',
    color: '#0F265C',
    fontWeight: 600,
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    textTransform: 'uppercase',
    fontSize: '1rem',
    marginBottom: '1rem',
    '&:hover': {
      backgroundColor: '#F0C000',
    },
    '&:disabled': {
      backgroundColor: '#cccccc',
      color: '#666666',
    },
  },
  guestButton: {
    color: '#FFFFFF',
    borderColor: '#FFFFFF',
    borderRadius: '4px',
    textTransform: 'none',
    marginTop: '0.5rem',
  },
  leftSection: {
    width: '40%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#1c2c50',
    color: '#FFFFFF',
    margin: 0,
    padding: '3rem',
    boxSizing: 'border-box',
    borderRight: 'none',
    '@media (max-width: 768px)': {
      width: '100%',
      padding: '2rem',
      borderRight: 'none',
    },
  },
  rightSection: {
    width: '60%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
    margin: 0,
    padding: 0,
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  rightImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
    display: 'block',
  },
  signInContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    maxWidth: '450px',
    backgroundColor: 'transparent',
  },
  logoContainer: {
    marginBottom: '2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  logo: {
    width: '120px',
    height: 'auto',
  },
  loginTitle: {
    fontSize: '1.8rem',
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: '0.5rem',
  },
  loginSubtitle: {
    fontSize: '1rem',
    color: 'rgba(255, 255, 255, 0.8)',
    marginBottom: '2.5rem',
  },
  errorMessage: {
    color: '#ff6b6b',
    marginTop: '1rem',
    textAlign: 'center',
    fontSize: '0.9rem',
  },
  loadingContainer: {
    width: 220,
    margin: '12px auto',
  },
}));

// Interfaz correcta para SignInPage
interface PichinchaSignInProps {
  onSignInSuccess: (identityApi: any) => void;
  auto?: boolean;
}

export const PichinchaSignIn = ({ 
  onSignInSuccess,
  auto = false 
}: PichinchaSignInProps) => {
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const microsoftAuth = useApi(microsoftAuthApiRef);
  
  const [loading, setLoading] = useState(false);
  const [autoShowPopup, setAutoShowPopup] = useState<boolean>(auto);
  const [retry, setRetry] = useState<{} | boolean | undefined>(true);
  const [error, setError] = useState<Error>();
  const [showLoginPage, setShowLoginPage] = useState<boolean>(false);
  
  // Check if we're in local environment to show guest login
  const isLocalEnv = configApi.getOptionalString('auth.environment') === 'local';
  
  // Function to create UserIdentity using the current pattern
  const createUserIdentity = async (authApi: any) => {
    const identityResponse = await authApi.getBackstageIdentity();
    const profile = await authApi.getProfile();
    
    return UserIdentity.create({
      identity: identityResponse.identity,
      authApi,
      profile,
    });
  };
  
  // Main effect to handle automatic authentication
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        setLoading(true);
        console.log('Checking existing session...');
        
        let identity;
        
        // Check if there is an active session
        identity = await microsoftAuth.getBackstageIdentity({
          optional: true,
        });

        // If there is no session and auto is true, show popup automatically
        if (!identity && autoShowPopup) {
          console.log('Showing automatic popup...');
          identity = await microsoftAuth.getBackstageIdentity({
            instantPopup: true,
          });
        }

        if (!identity) {
          console.log('No active session, showing login page');
          setShowLoginPage(true);
          setLoading(false);
          return;
        }

        console.log('Existing session found, creating UserIdentity...');
        
        // Create UserIdentity using the current pattern
        const userIdentity = await createUserIdentity(microsoftAuth);
        onSignInSuccess(userIdentity);
        
      } catch (err: any) {
        console.error('Error during session verification:', err);
        setError(err);
        setShowLoginPage(true);
      } finally {
        setLoading(false);
      }
    };

    if (retry) {
      checkExistingSession();
    }
  }, [microsoftAuth, retry, autoShowPopup, onSignInSuccess]);

  // Manual Microsoft login handler
  const handleMicrosoftLogin = async () => {
    try {
      setLoading(true);
      setError(undefined);
      console.log('Starting Microsoft authentication flow...');
      
      // Get identity using popup
      const identity = await microsoftAuth.getBackstageIdentity({
        instantPopup: true,
      });
      
      if (!identity) {
        throw new Error('Could not get Backstage identity');
      }
      
      console.log('Identity obtained, creating UserIdentity...');
      
      // Create UserIdentity using the current pattern
      const userIdentity = await createUserIdentity(microsoftAuth);
      console.log('UserIdentity created successfully');
      
      // Call callback with UserIdentity
      onSignInSuccess(userIdentity);
      
    } catch (err: any) {
      console.error('Error during login:', err);
      if (err.name !== 'PopupRejectedError') {
        setError(err);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Guest login handler
  const handleGuestLogin = () => {
    console.log('Logging in as guest...');
    
    // Create guest UserIdentity
    const guestIdentity = UserIdentity.createGuest();
    onSignInSuccess(guestIdentity);
  };

  // Show initial loading
  if (loading && !showLoginPage) {
    return (
      <div className={classes.container}>
        <div className={classes.leftSection}>
          <div className={classes.signInContainer}>
            <div className={classes.loadingContainer}>
              <Progress />
            </div>
            <Typography variant="body1" style={{ marginTop: '1rem', textAlign: 'center' }}>
              Checking existing session...
            </Typography>
          </div>
        </div>
        <div className={classes.rightSection}>
          <img
            src="side-photo.png"
            alt="Illustration"
            className={classes.rightImage}
          />
        </div>
      </div>
    );
  }

  // Main login page
  return (
    <div className={classes.container}>
      <div className={classes.leftSection}>
        <div className={classes.signInContainer}>
          {/* Logo */}
          <div className={classes.logoContainer}>
            <img
              src="logoidp.png"
              alt="BANKDEVX Logo"
              className={classes.logo}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = 'logo192.png';
                console.log('Using fallback image logo192.png');
              }}
            />
          </div>
          
          {/* Title and Subtitle */}
          <Typography variant="h1" className={classes.loginTitle}>
            Welcome to BANKDEVX
          </Typography>
          
          <Typography variant="body1" className={classes.loginSubtitle}>
            Internal Developer Portal
          </Typography>
          
          {/* Microsoft Login Button */}
          {loading ? (
            <div className={classes.loadingContainer}>
              <Progress />
            </div>
          ) : (
            <Button 
              variant="contained"
              fullWidth 
              onClick={handleMicrosoftLogin}
              className={classes.microsoftButton}
              disabled={loading}
            >
              Sign in with Microsoft
            </Button>
          )}
          
          {/* Guest Login Button (only in local environment) */}
          {isLocalEnv && (
            <Button
              variant="outlined"
              fullWidth
              onClick={handleGuestLogin}
              className={classes.guestButton}
              disabled={loading}
            >
              Sign in as guest
            </Button>
          )}
          
          {/* Error Message */}
          {error && error.name !== 'PopupRejectedError' && (
            <Typography variant="body2" className={classes.errorMessage}>
              Error: {error.message}
            </Typography>
          )}

          {/* Retry Button */}
          {error && (
            <Button
              variant="text"
              onClick={() => {
                setRetry({});
                setAutoShowPopup(true);
                setError(undefined);
              }}
              style={{ marginTop: '1rem', color: '#FFFFFF' }}
            >
              Retry
            </Button>
          )}
        </div>
      </div>
      <div className={classes.rightSection}>
        <img
          src="side-photo.png"
          alt="Illustration"
          className={classes.rightImage}
        />
      </div>
    </div>
  );
};